import {
  ModuleCategory,
  OptionalString,
  PublishesTo,
  SchemaTypeHelper,
  StringEnum,
  SubscribesTo,
  createComponentAdmin,
} from '@backstage-components/base';
import {Static, Type} from '@sinclair/typebox';

export const reactName = 'Intercom';
export const name = 'Intercom Support Plugin';
const category: ModuleCategory = 'plugin';

const schema = Type.Object({
  appId: Type.String({
    title: 'Intercom App Id',
    description: 'The APP_ID from Intercom.',
  }),
  apiBase: Type.String({
    title: 'Intercom API Url',
    description:
      'api_base from Intercom settings page in the "with code" integration',
    default: 'https://api-iam.intercom.io',
  }),
  alignment: StringEnum(['right', 'left'] as const, {
    title: 'Intercom launcher alignment',
    description:
      'Dictate the alignment of the default launcher icon to be on the left/right.',
    default: 'right',
  }),
  backgroundColor: OptionalString({
    title: 'Intercom background color',
    description: 'Used behind your team profile and other attributes',
  }),
  actionColor: OptionalString({
    title: 'Intercom action color',
    description: 'Used in button links and more to highlight and emphasise',
  }),
});

const uiSchema = {
  backgroundColor: {'ui:widget': 'color'},
  actionColor: {'ui:widget': 'color'},
};

const defaultFieldData: Static<typeof schema> = {
  appId: schema.properties.appId.default,
  alignment: schema.properties.alignment.default,
  apiBase: schema.properties.apiBase.default,
};

const instructions = Type.Union([
  SubscribesTo({
    topic: `${reactName}:boot`,
    description: 'Used to ensure Intercom has been loaded',
  }),
  SubscribesTo({
    topic: `${reactName}:hide`,
    description: 'Explicitly hide the Intercom messenger.',
  }),
  SubscribesTo({
    topic: `${reactName}:show`,
    description: 'Explicitly show the Intercom messenger.',
  }),
  SubscribesTo({
    topic: `${reactName}:showMessages`,
    description: 'Explicitly show the Intercom messenger with message list.',
  }),
  SubscribesTo({
    topic: `${reactName}:shutdown`,
    description: 'Completely remove the Intercom messenger.',
  }),
  SubscribesTo({
    topic: `${reactName}:trackEvent`,
    description:
      'Associate the event with the currently logged in user and send it to Intercom.',
    meta: {
      name: Type.String({
        title: 'Intercom Event Name',
        description: 'Name of the event to associate with the user.',
      }),
    },
  }),
  SubscribesTo({
    topic: `${reactName}:update`,
    description:
      'Update the Intercom widget to register a URL or route change.',
  }),
  PublishesTo({
    topic: `${reactName}:on-hide`,
    description: 'Broadcast when the Intercom messenger is hidden.',
  }),
  PublishesTo({
    topic: `${reactName}:on-show`,
    description: 'Broadcast when the Intercom messenger is displayed.',
  }),
  PublishesTo({
    topic: `${reactName}:on-unread-count-change`,
    description:
      'Broadcast when the Intercom unread count changes, this could include changing the count to zero.',
    meta: {
      count: Type.Integer({
        description: 'The current number of unread messages.',
      }),
    },
  }),
]);

export const ComponentDefinition = createComponentAdmin({
  id: '9a8c256c-5fa4-47da-8e4b-a0c402e7e24f',
  reactName,
  name,
  slug: reactName,
  description: 'Should exist directly in page and only have one instance.',
  version: 1,
  defaultFieldData,
  slotConfiguration: {},
  schema,
  uiSchema,
  instructions,
  category,
}).build();

export type SchemaType = SchemaTypeHelper<typeof ComponentDefinition>;
